import { Vec3, vec3, add, sub, scale, cross, dot, length, normalize, ZERO } from '../math/vec3'
import { rotate, unrotate } from '../math/quat'
import { CelestialBody } from '../world/body'
import { Vessel } from '../vessel/vessel'
import { PartCatalog, partDef } from '../vessel/catalog'
import { MassProperties } from '../vessel/massProperties'
import { RigidState } from '../dynamics/rigidBody'
import { altitudeOf, atmosphereVelocity } from '../dynamics/flight'
import { localUp } from '../nav/attitude'

export interface PuntoContatto {
  readonly partId: string
  /** posizione nel sistema del veicolo */
  readonly locale: Vec3
}

export interface ParametriContatto {
  /** rigidezza della molla di contatto, N/m */
  readonly rigidezza: number
  /** smorzamento, N·s/m */
  readonly smorzamento: number
  /** coefficiente d'attrito */
  readonly attrito: number
}

export const CONTATTO_STANDARD: ParametriContatto = {
  rigidezza: 2.2e5,
  smorzamento: 2.4e4,
  attrito: 0.7,
}

/** I piedi delle gambe, portati in coordinate del veicolo. */
export function puntiDiContatto(vessel: Vessel, catalog: PartCatalog): PuntoContatto[] {
  const out: PuntoContatto[] = []
  for (const p of vessel.parts) {
    const piede = partDef(catalog, p.defId).footPoint
    if (!piede) continue
    out.push({ partId: p.id, locale: add(p.position, rotate(p.rotation, piede)) })
  }
  return out
}

export interface ForzaContatto {
  readonly partId: string
  /** forza nel sistema del VEICOLO */
  readonly force: Vec3
  /** punto d'applicazione nel sistema del veicolo */
  readonly point: Vec3
  readonly penetrazione: number
}

/**
 * Forze di contatto ai piedi.
 *
 * Molla-smorzatore in compressione piu attrito di Coulomb. Il ribaltamento
 * non e programmato da nessuna parte: se il baricentro esce dal poligono
 * d'appoggio, i momenti di queste forze fanno il resto da soli — che e
 * esattamente come si ribalta un lander vero.
 */
export function forzeDiContatto(
  punti: readonly PuntoContatto[],
  state: RigidState,
  massProps: MassProperties,
  body: CelestialBody,
  parametri: ParametriContatto = CONTATTO_STANDARD,
): ForzaContatto[] {
  const out: ForzaContatto[] = []
  const suolo = atmosphereVelocity(state.position, body)

  for (const punto of punti) {
    const rel = sub(punto.locale, massProps.com)
    const mondo = add(state.position, rotate(state.orientation, rel))
    const quota = altitudeOf(mondo, body)
    if (quota >= 0) continue

    const penetrazione = -quota
    const up = localUp(mondo)

    // velocita del punto: quella del corpo piu quella dovuta alla rotazione
    const vPunto = add(state.velocity, rotate(state.orientation, cross(state.angularVelocity, rel)))
    const vRel = sub(vPunto, suolo)
    const vNormale = dot(vRel, up)

    // molla-smorzatore, ma solo in compressione: il terreno non tira
    const normale = Math.max(0, parametri.rigidezza * penetrazione - parametri.smorzamento * vNormale)
    if (normale <= 0) continue

    let forzaMondo = scale(up, normale)

    const vTangente = sub(vRel, scale(up, vNormale))
    const velocitaTangente = length(vTangente)
    if (velocitaTangente > 1e-6) {
      // attrito di Coulomb regolarizzato: sotto una soglia si comporta come
      // una molla viscosa, cosi un lander fermo non vibra
      const modulo = Math.min(parametri.attrito * normale, velocitaTangente * parametri.smorzamento * 0.5)
      forzaMondo = add(forzaMondo, scale(normalize(vTangente), -modulo))
    }

    out.push({
      partId: punto.partId,
      force: unrotate(state.orientation, forzaMondo),
      point: punto.locale,
      penetrazione,
    })
  }
  return out
}

/** Vero se almeno un piede tocca terra. */
export const aTerra = (forze: readonly ForzaContatto[]): boolean => forze.length > 0

/** Il raggio del poligono d'appoggio: quanto e largo il carrello. */
export function raggioAppoggio(punti: readonly PuntoContatto[], com: Vec3): number {
  let massimo = 0
  for (const p of punti) {
    massimo = Math.max(massimo, Math.hypot(p.locale.x - com.x, p.locale.z - com.z))
  }
  return massimo
}
